/**
 * Prompt Executor — natural language routing via LLM tool selection.
 *
 * Converts component operations into tool descriptions, sends them to an LLM
 * for routing, and dispatches the selected operation through the existing
 * component binding.
 */

import * as YAML from "yaml";
import type { ComponentIR, JSONSchemaRef } from "@aaac/contracts";
import type { RuntimeBinding, AaacInvocationContext } from "../component-binding.js";
import { createAdapter } from "./adapter-factory.js";

export interface OperationToolDef {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

export interface PromptResult {
  selectedOperation: string;
  mappedInput: Record<string, unknown>;
  output?: unknown;
  dryRun?: boolean;
  model?: string;
}

export interface PromptExecuteOptions {
  dryRun?: boolean;
  adapter: string;
  model?: string;
}

function unwrapInputSchema(
  input: JSONSchemaRef | undefined,
): Record<string, unknown> {
  if (!input) {
    return { type: "object", properties: {} };
  }
  if ("schema" in input && input.schema && typeof input.schema === "object") {
    return input.schema as Record<string, unknown>;
  }
  if ("$ref" in input) {
    return { type: "object", properties: {} };
  }
  return input as Record<string, unknown>;
}

/**
 * Convert non-handler, non-prompt operations from ComponentIR into tool definitions
 * for LLM routing.
 */
export function operationsToTools(ir: ComponentIR): OperationToolDef[] {
  return Object.entries(ir.operations)
    .filter(([name, op]) => name !== "prompt" && !op.handler)
    .map(([name, op]) => ({
      name,
      description: op.description,
      inputSchema: unwrapInputSchema(op.input),
    }));
}

function buildRouterPrompt(
  componentId: string,
  tools: OperationToolDef[],
  message: string,
): string {
  const toolDescriptions = tools
    .map((t) => {
      const schema = JSON.stringify(t.inputSchema, null, 2);
      return `### ${t.name}\n${t.description}\nInput schema:\n\`\`\`json\n${schema}\n\`\`\``;
    })
    .join("\n\n");

  return [
    `You are an operation router for the "${componentId}" CLI.`,
    `Understand the user request and call the appropriate operation.`,
    `Always select exactly one operation. Extract parameters from the user message.`,
    ``,
    `## Available Operations`,
    ``,
    toolDescriptions,
    ``,
    `## Response Format`,
    ``,
    `Respond with ONLY the following YAML:`,
    ``,
    "```yaml",
    `operation: <operation_name>`,
    `input:`,
    `  <key>: <value>`,
    "```",
    ``,
    `## User Request`,
    ``,
    message,
  ].join("\n");
}

/**
 * Parse the LLM router response to extract selected operation and mapped input.
 */
export function parseRouterResponse(
  response: string,
  validNames: string[],
): { selectedOperation: string; mappedInput: Record<string, unknown> } {
  const yamlMatch = response.match(/```(?:yaml)?\n([\s\S]*?)```/);
  const content = yamlMatch ? yamlMatch[1].trim() : response.trim();

  let parsed: Record<string, unknown> | undefined;
  try {
    parsed = YAML.parse(content) as Record<string, unknown>;
  } catch {
    const jsonMatch = response.match(/\{[\s\S]*"operation"[\s\S]*\}/);
    if (jsonMatch) {
      try {
        parsed = JSON.parse(jsonMatch[0]) as Record<string, unknown>;
      } catch {
        // fall through to error below
      }
    }
  }

  if (parsed?.operation && typeof parsed.operation === "string") {
    if (!validNames.includes(parsed.operation)) {
      throw new Error(
        `Router selected unknown operation "${parsed.operation}". Available: ${validNames.join(", ")}`,
      );
    }
    return {
      selectedOperation: parsed.operation,
      mappedInput: (parsed.input ?? {}) as Record<string, unknown>,
    };
  }

  throw new Error(
    `Could not parse operation routing from LLM response. ` +
      `Expected YAML with "operation" and "input" fields.\n` +
      `Response:\n${response.slice(0, 500)}`,
  );
}

/**
 * Execute a natural language prompt by routing to the appropriate operation.
 */
export async function executePrompt(
  message: string,
  ir: ComponentIR,
  binding: RuntimeBinding,
  ctx: AaacInvocationContext,
  opts: PromptExecuteOptions,
): Promise<PromptResult> {
  const tools = operationsToTools(ir);
  if (tools.length < 2) {
    throw new Error(
      `prompt command requires at least 2 routable operations, found ${tools.length}`,
    );
  }

  const prompt = buildRouterPrompt(ir.info.id, tools, message);
  const adapter = await createAdapter(opts.adapter, { model: opts.model });
  const response = await adapter.send(prompt, { readonly: true });
  const { selectedOperation, mappedInput } = parseRouterResponse(
    response,
    tools.map((t) => t.name),
  );

  if (opts.dryRun) {
    return {
      selectedOperation,
      mappedInput,
      dryRun: true,
      model: opts.model,
    };
  }

  const output = await binding.execute(selectedOperation, {}, mappedInput, ctx);
  return { selectedOperation, mappedInput, output };
}
